import { ActionMenu, ActionMenuItem, FormInput, Hint } from '@systemeio/ui'
import {
  removeCourseBundleItem,
  updateCourseBundleItem,
} from 'modules/courses/bundles/course-bundle/api/course-bundle-api'
import { useCoursesList } from 'modules/courses/bundles/course-bundle/hooks/use-courses-list'
import {
  CourseBundleInterface,
  CourseBundleItemInterface,
} from 'modules/courses/bundles/course-bundle/types/course-bundle-interface'
import { getSortedCourseList } from 'modules/courses/bundles/course-bundle/utils/get-sorted-course-list'
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import Confirmation from 'shared/components/confirmation-modal'
import EditModal from 'shared/components/edit-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors'
import { KeyedMutator } from 'swr'

interface CourseActionsProps {
  item: CourseBundleItemInterface
  mutate: KeyedMutator<CourseBundleInterface>
}

const defaultErrors = {
  fields: {
    delayBeforeStart: '',
  },
}

function CourseActions({ item, mutate }: CourseActionsProps) {
  const { t } = useLocoTranslation()
  const router = useRouter()
  const { courseBundleId } = router.query
  const { mutate: mutateCoursesList } = useCoursesList(courseBundleId as string)

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isRemoveOpen, setIsRemoveOpen] = useState(false)
  const [settingErrors, setSettingErrors] = useState(defaultErrors)

  const caption = t('dashboard.course_bundle.course.title')

  const onRemoveCourseBundleItem = async (itemId: number) => {
    await removeCourseBundleItem(itemId)
    await mutate(data => {
      if (data) {
        return {
          ...data,
          items: data.items.filter(item => item.id !== itemId),
        }
      }
    }, false)
    await mutateCoursesList(data => {
      if (data) {
        return getSortedCourseList([...data, item.course])
      }
    }, false)
  }

  const onEditCourse = async (
    itemId: number,
    delayBeforeStart: Pick<CourseBundleItemInterface, 'delayBeforeStart'>,
  ) => {
    await fetchWithFieldErrors(async () => {
      await updateCourseBundleItem(delayBeforeStart, itemId)
      await mutate(data => {
        if (data) {
          return {
            ...data,
            items: data.items.map(item => {
              if (item.id === itemId) {
                return {
                  ...item,
                  ...delayBeforeStart,
                }
              }
              return item
            }),
          }
        }
      }, false)
    }, setSettingErrors)
  }

  return (
    <div className="flex items-center lg:justify-end">
      <div {...nonDraggableProps}>
        <ActionMenu
          menuItems={[
            <ActionMenuItem
              key="settings"
              label={t('dashboard.actions.title.settings')}
              onClick={() => {
                setIsSettingsOpen(true)
              }}
            />,
            <ActionMenuItem
              key="remove"
              label={t('dashboard.actions.title.remove')}
              onClick={() => {
                setIsRemoveOpen(true)
              }}
            />,
          ]}
        />
        <EditModal
          fullCaption={t('dashboard.course_bundle.course.edit_delay_title')}
          opened={isSettingsOpen}
          onClose={() => {
            setIsSettingsOpen(false)
          }}
          instance={item}
          toastCaption={t('global.changes_saved')}
          editable={[
            {
              field: 'delayBeforeStart',
              onRender: (value, onChange) => (
                <FormInput
                  key="course-edit"
                  value={value}
                  onChange={e => onChange(Number(e.target.value))}
                  type="number"
                  label={
                    <Hint label={t('dashboard.course_bundle.course.delay_title_tooltip')}>
                      {t('dashboard.course_bundle.course.delay_before_start_title')}
                    </Hint>
                  }
                  error={settingErrors.fields.delayBeforeStart}
                />
              ),
            },
          ]}
          onEdit={data => onEditCourse(item.id, data)}
        />
        <Confirmation
          title={t('dashboard.course_bundle.course.remove_confirmation_title')}
          confirmationText={t('dashboard.course_bundle.course.remove_confirmation_text')}
          toastCaption={t('dashboard.actions.removed', { module: caption?.toLowerCase() })}
          opened={isRemoveOpen}
          onClose={() => setIsRemoveOpen(false)}
          onConfirm={() => onRemoveCourseBundleItem(item.id)}
        />
      </div>
    </div>
  )
}

export default CourseActions
